/**
 * Mux player presentation — shared poster URLs + no native bottom controls.
 *
 * Product rule (visitor-facing Mux surfaces):
 *   1. Show a Mux thumbnail (LQIP → full) while the stream buffers.
 *   2. Never show the native Mux control bar at the bottom.
 *   3. Prefer custom UI (CTA, mute chip, dual panel) over ::part(bottom).
 */

/** CDN-friendly width buckets for image.mux.com thumbnails. */
export function muxPosterWidthBucket(opts?: {
  mobile?: boolean;
  max?: number;
}): number {
  if (typeof window === "undefined") return opts?.mobile ? 720 : 1080;
  const dpr = window.devicePixelRatio || 1;
  const w = window.innerWidth * dpr;
  const cap = opts?.max ?? (opts?.mobile ? 1080 : 1920);
  const optimal = Math.min(w, cap);
  if (optimal <= 480) return 480;
  if (optimal <= 720) return 720;
  if (optimal <= 1080) return 1080;
  return 1440;
}

export type MuxPosterFitMode = "smartcrop" | "preserve" | "crop" | "pad";

export function buildMuxThumbnailUrl(
  playbackId: string | null | undefined,
  opts?: {
    time?: number;
    width?: number;
    quality?: number;
    fitMode?: MuxPosterFitMode;
  },
): string | null {
  const pid = String(playbackId || "").trim();
  if (!pid) return null;
  const time = opts?.time ?? 0;
  const width = opts?.width ?? muxPosterWidthBucket();
  const quality = opts?.quality ?? 72;
  const fitMode = opts?.fitMode ?? "smartcrop";
  return `https://image.mux.com/${encodeURIComponent(pid)}/thumbnail.webp?time=${time}&width=${width}&quality=${quality}&fit_mode=${fitMode}`;
}

export function buildMuxLqipUrl(
  playbackId: string | null | undefined,
  opts?: { time?: number; fitMode?: MuxPosterFitMode },
): string | null {
  return buildMuxThumbnailUrl(playbackId, {
    time: opts?.time ?? 0,
    width: 40,
    quality: 30,
    fitMode: opts?.fitMode ?? "smartcrop",
  });
}

/**
 * CSS custom properties for mux-player — hide the entire native chrome.
 * Apply on the element style and/or a scoped stylesheet.
 */
export const MUX_HIDE_NATIVE_CONTROLS_VARS = {
  "--controls": "none",
  "--center-play-button": "none",
  "--bottom-play-button": "none",
  "--seek-backward-button": "none",
  "--seek-forward-button": "none",
  "--mute-button": "none",
  "--captions-button": "none",
  "--airplay-button": "none",
  "--pip-button": "none",
  "--fullscreen-button": "none",
  "--cast-button": "none",
  "--playback-rate-button": "none",
  "--volume-range": "none",
  "--time-range": "none",
  "--time-display": "none",
  "--duration-display": "none",
  "--live-button": "none",
  "--top-controls": "none",
  "--media-control-bar": "none",
} as const;

/**
 * Scoped CSS: hide control bar parts that still paint when CSS vars alone fail
 * (Instagram WebView / some mux-player versions).
 *
 * @param selector e.g. `.mux-video-player-cta-xyz mux-player` or `.reels-mux-player`
 */
export function muxHideNativeControlsCss(selector: string): string {
  return `
    ${selector} {
      --controls: none !important;
      --center-play-button: none !important;
      --bottom-play-button: none !important;
      --seek-backward-button: none !important;
      --seek-forward-button: none !important;
      --mute-button: none !important;
      --captions-button: none !important;
      --airplay-button: none !important;
      --pip-button: none !important;
      --fullscreen-button: none !important;
      --cast-button: none !important;
      --playback-rate-button: none !important;
      --volume-range: none !important;
      --time-range: none !important;
      --time-display: none !important;
      --duration-display: none !important;
      --live-button: none !important;
      --top-controls: none !important;
    }
    /* Never show the bottom control strip at the bottom of the viewport */
    ${selector}::part(bottom),
    ${selector}::part(control-bar),
    ${selector}::part(layer bottom),
    ${selector}::part(center play button) {
      display: none !important;
      opacity: 0 !important;
      pointer-events: none !important;
      visibility: hidden !important;
      height: 0 !important;
      max-height: 0 !important;
    }
  `;
}
